import type { DiaryEntry } from "@couples/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bookmark } from "lucide-react";
import { useMemo, useState } from "react";

import DiaryCard from "../components/DiaryCard";
import DiaryEntrySheet from "../components/DiaryEntrySheet";
import OverlayShell from "../components/OverlayShell";
import QueryState from "../components/QueryState";
import { errorMessageOf, useAuth } from "../lib/auth";
import { colorOfUser } from "../lib/color";
import { useConfirm } from "../lib/confirm";
import {
  fetchBookmarkedEntryIds,
  fetchMutualBookmarkedEntryIds,
  removeBookmark,
} from "../lib/diary/bookmarks";
import { deleteEntry, fetchEntries, updateEntry, type EntryInput } from "../lib/diary/entries";
import { fetchEntryMediaIds, updateEntryMedia } from "../lib/diary/media";
import { fetchPrompts } from "../lib/diary/prompts";
import { fetchMembers } from "../lib/media/members";
import { fetchMediaByIds } from "../lib/media/notifications";
import { useMediaThumbnails } from "../lib/media/useMediaThumbnails";
import { fetchCoupleProfiles } from "../lib/profileItems";
import { queryKeys } from "../lib/queryKeys";
import { useToast } from "../lib/toast";
import MediaDetail from "./MediaDetail";

function chipClass(active: boolean): string {
  return active
    ? "bg-[var(--color-teal)] text-white"
    : "border border-[var(--color-line)] text-[var(--color-sub)]";
}

/** しおりを挟んだ日記だけを集めた面。並びは日記フィードと同じ新しい順 */
export default function DiaryBookmarks({ onClose }: { onClose: () => void }) {
  const { session } = useAuth();
  const queryClient = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const selfId = session?.user.id;
  const [opened, setOpened] = useState<{ ids: string[]; currentId: string } | null>(null);
  const [editing, setEditing] = useState<DiaryEntry | null>(null);
  const [savingEdit, setSavingEdit] = useState(false);
  // 「自分」= 自分のしおり一覧(既存)。「二人」= 両方が挟んだものだけ(誰が挟んだかは非公開のまま)
  const [tab, setTab] = useState<"own" | "mutual">("own");

  const members = useQuery({ queryKey: queryKeys.members, queryFn: fetchMembers });
  const profiles = useQuery({ queryKey: queryKeys.coupleProfiles, queryFn: fetchCoupleProfiles });
  const prompts = useQuery({ queryKey: queryKeys.diaryPrompts, queryFn: fetchPrompts });
  const entries = useQuery({ queryKey: queryKeys.diaryEntries, queryFn: fetchEntries });
  const bookmarkIds = useQuery({
    queryKey: queryKeys.diaryBookmarkIds,
    queryFn: fetchBookmarkedEntryIds,
  });
  const mutualBookmarkIds = useQuery({
    queryKey: queryKeys.diaryMutualBookmarkIds,
    queryFn: fetchMutualBookmarkedEntryIds,
    enabled: tab === "mutual",
  });
  const entryMediaIds = useQuery({
    queryKey: queryKeys.diaryEntryMedia,
    queryFn: fetchEntryMediaIds,
  });

  const allMediaIds = useMemo(
    () => [...new Set([...(entryMediaIds.data?.values() ?? [])].flat())],
    [entryMediaIds.data],
  );
  const thumbs = useMediaThumbnails(allMediaIds);

  const openedMedia = useQuery({
    queryKey: ["diary-entry-media-detail", opened?.ids],
    queryFn: () => fetchMediaByIds(opened?.ids ?? []),
    enabled: opened !== null,
  });

  const promptById = useMemo(
    () => new Map((prompts.data ?? []).map((prompt) => [prompt.id, prompt])),
    [prompts.data],
  );

  const bookmarked = useMemo(
    () => (entries.data ?? []).filter((entry) => bookmarkIds.data?.has(entry.id) === true),
    [entries.data, bookmarkIds.data],
  );
  const mutual = useMemo(
    () => (entries.data ?? []).filter((entry) => mutualBookmarkIds.data?.has(entry.id) === true),
    [entries.data, mutualBookmarkIds.data],
  );
  const visible = tab === "own" ? bookmarked : mutual;

  const colorOf = (authorId: string) => colorOfUser(profiles.data, authorId, selfId);

  const unbookmark = useMutation({
    mutationFn: (entry: DiaryEntry) => removeBookmark(entry.id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.diaryBookmarks });
      toast("しおりを外しました。");
    },
    onError: (error) => {
      toast(errorMessageOf(error), "error");
    },
  });

  const removeEntry = useMutation({
    mutationFn: deleteEntry,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.diaryEntries });
      await queryClient.invalidateQueries({ queryKey: queryKeys.diaryBookmarks });
      toast("日記を削除しました。");
    },
    onError: (error) => {
      toast(errorMessageOf(error), "error");
    },
  });

  async function handleDelete(entry: DiaryEntry) {
    const ok = await confirm({
      message: "この日記を消します。",
      confirmLabel: "日記を消す",
      tone: "danger",
    });
    if (!ok) return;
    removeEntry.mutate(entry.id);
  }

  async function handleEditSave(input: EntryInput, mediaIds: string[]) {
    if (editing === null) return;
    setSavingEdit(true);
    try {
      await updateEntry(editing.id, input);
      await updateEntryMedia(editing.id, entryMediaIds.data?.get(editing.id) ?? [], mediaIds);
      await queryClient.invalidateQueries({ queryKey: queryKeys.diaryEntries });
      await queryClient.invalidateQueries({ queryKey: queryKeys.diaryEntryMedia });
      setEditing(null);
      toast("日記を直しました。");
    } catch (e) {
      toast(errorMessageOf(e), "error");
    } finally {
      setSavingEdit(false);
    }
  }

  const loading =
    entries.isPending || (tab === "own" ? bookmarkIds.isPending : mutualBookmarkIds.isPending);
  const failed =
    entries.isError || (tab === "own" ? bookmarkIds.isError : mutualBookmarkIds.isError);

  return (
    <OverlayShell title="しおり" icon={Bookmark} onClose={onClose} width="list">
      <div className="flex flex-col gap-3 p-4">
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => {
              setTab("own");
            }}
            aria-pressed={tab === "own"}
            className={`rounded-full px-3 py-1 text-xs ${chipClass(tab === "own")}`}
          >
            自分
          </button>
          <button
            type="button"
            onClick={() => {
              setTab("mutual");
            }}
            aria-pressed={tab === "mutual"}
            className={`rounded-full px-3 py-1 text-xs ${chipClass(tab === "mutual")}`}
          >
            二人
          </button>
        </div>

        <QueryState
          isPending={loading}
          isError={failed}
          errorMessage="しおりの取得に失敗しました。"
          isEmpty={visible.length === 0}
          emptyMessage={
            tab === "own"
              ? "しおりを挟んだ日記はありません。"
              : "二人ともしおりを挟んだ日記はまだありません。"
          }
          emptyClassName="py-10 text-center text-sm"
        />
        {visible.map((entry) => (
          <DiaryCard
            key={entry.id}
            entry={entry}
            authorName={members.data?.get(entry.authorId) ?? "?"}
            authorColor={colorOf(entry.authorId)}
            prompt={entry.promptId !== null ? promptById.get(entry.promptId) : undefined}
            isBookmarked
            onToggleBookmark={() => void unbookmark.mutate(entry)}
            onEdit={
              entry.authorId === selfId
                ? () => {
                    setEditing(entry);
                  }
                : undefined
            }
            onDelete={entry.authorId === selfId ? () => void handleDelete(entry) : undefined}
            mediaIds={entryMediaIds.data?.get(entry.id) ?? []}
            thumbUrlOf={thumbs.urlOf}
            onOpenMedia={(mediaId) => {
              setOpened({ ids: entryMediaIds.data?.get(entry.id) ?? [], currentId: mediaId });
            }}
          />
        ))}
      </div>

      {opened !== null && openedMedia.data !== undefined && openedMedia.data.length > 0 && (
        <MediaDetail
          items={openedMedia.data}
          currentId={opened.currentId}
          thumbnailUrls={thumbs.urls}
          onChange={(id) => {
            setOpened((current) => (current === null ? null : { ...current, currentId: id }));
          }}
          onClose={() => {
            setOpened(null);
          }}
        />
      )}

      {editing !== null && (
        <DiaryEntrySheet
          prompts={prompts.data ?? []}
          editingEntry={editing}
          initialMediaIds={entryMediaIds.data?.get(editing.id) ?? []}
          busy={savingEdit}
          onSave={(input, mediaIds) => void handleEditSave(input, mediaIds)}
          onClose={() => {
            setEditing(null);
          }}
        />
      )}
    </OverlayShell>
  );
}
